import { loginHref } from "@/lib/next-path";

const API_BASE_URL =
  process.env.API_URL ?? "https://customer-api.fastsvc.net";
const CLIENT_PROXY_PREFIX = "/api/proxy";

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  token?: string;
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  message: string;
  errors: unknown[];
  metadata: unknown;
}

// Shared promise so concurrent 401s trigger exactly one refresh call.
let _refreshPromise: Promise<boolean> | null = null;
// Once refresh fails, mark session dead so React Query retries don't hammer
// the refresh endpoint with a rotating token (each failed attempt could
// invalidate the token server-side).
let _sessionDead = false;

/** Call this after a successful login so the dead-session guard is lifted. */
export function resetSessionDead() {
  _sessionDead = false;
}

function refreshSession(): Promise<boolean> {
  if (_sessionDead) return Promise.resolve(false);
  if (!_refreshPromise) {
    _refreshPromise = fetch("/api/auth/refresh", {
      method: "POST",
      credentials: "include",
    })
      .then((r) => {
        if (!r.ok) _sessionDead = true;
        return r.ok;
      })
      .catch(() => { _sessionDead = true; return false; })
      .finally(() => { _refreshPromise = null; });
  }
  return _refreshPromise;
}

async function request<T>(
  endpoint: string,
  { body, token, headers: customHeaders, ...init }: RequestOptions = {}
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((customHeaders as Record<string, string>) ?? {}),
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  // If we're running in the browser and no explicit token was provided,
  // route the request through the Next.js server proxy so httpOnly cookies
  // can be read server-side and forwarded to the backend.
  const isClient = typeof window !== "undefined";
  let res: Response;

  const fetchOptions = (): [string, RequestInit] => {
    if (isClient && !token) {
      let clientUrl: string;
      if (endpoint === "/me/corporate") {
        clientUrl = "/api/entreprise";
      } else if (endpoint === "/me/corporate/member-wallet") {
        clientUrl = "/api/entreprise?member_wallet=1";
      } else {
        clientUrl = `${CLIENT_PROXY_PREFIX}${endpoint}`;
      }
      return [clientUrl, { ...init, headers, body: body ? JSON.stringify(body) : undefined, credentials: "include" }];
    }
    return [`${API_BASE_URL}${endpoint}`, { ...init, headers, body: body ? JSON.stringify(body) : undefined }];
  };

  const [url, opts] = fetchOptions();
  res = await fetch(url, opts);

  // On 401 (browser requests only), attempt a single token refresh then retry.
  if (res.status === 401 && isClient && !token) {
    const refreshed = await refreshSession();
    if (refreshed) {
      res = await fetch(url, opts);
    } else {
      // Session is dead — clear local auth state and redirect to login.
      // Dynamic import avoids a circular dep between api-client and auth.store.
      import("@/store/auth.store").then(({ useAuthStore }) => {
        useAuthStore.getState().clearAuth();
      });
      const onAuthPage = window.location.pathname.startsWith("/login") ||
        window.location.pathname.startsWith("/auth") ||
        window.location.pathname.startsWith("/register");
      if (!onAuthPage) {
        // Une session qui expire au milieu d'un panier ne doit pas coûter la
        // page en cours : on repasse par la connexion et on revient ici.
        window.location.href = loginHref(
          `${window.location.pathname}${window.location.search}`,
        );
      }
    }
  }

  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: res.statusText }));
    throw new ApiError(res.status, error.message ?? "Something went wrong", error);
  }

  // Handle 204 No Content
  if (res.status === 204) {
    return undefined as T;
  }

  const json = await res.json();

  // Unwrap API envelope { success, data, message, errors, metadata }
  if (json && typeof json === "object" && "success" in json && "data" in json) {
    return json.data as T;
  }

  return json as T;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export const api = {
  get: <T>(endpoint: string, opts?: RequestOptions) =>
    request<T>(endpoint, { ...opts, method: "GET" }),

  post: <T>(endpoint: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(endpoint, { ...opts, method: "POST", body }),

  patch: <T>(endpoint: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(endpoint, { ...opts, method: "PATCH", body }),

  put: <T>(endpoint: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(endpoint, { ...opts, method: "PUT", body }),

  delete: <T>(endpoint: string, opts?: RequestOptions) =>
    request<T>(endpoint, { ...opts, method: "DELETE" }),
};
